import styles from './TyreSizePicker.module.scss'
import React, { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { useGetTyreSizesQuery, useLazyGetDiametersQuery } from '@/api/tyreSizes'
import NameValue from '@/types/NameValue'
import FilterableSelect from '@/components/inputs/FilterableSelect/FilterableSelect'

type Props = {
  tyreType: number,
  width?: number,
  aspectRatio?: number,
  diameter?: number,
  onCriteriaChange: (criteria: TyreSizeCriteria) => void,
}

export interface TyreSizeCriteria {
  tyreType: number,
  width?: number,
  aspectRatio?: number,
  diameter?: number,
}

const TyreSizePicker = (props: Props) => {
  const { t } = useTranslation()
  const { data: tyreSizesData } = useGetTyreSizesQuery()
  const [fetchDiameters, diametersData] = useLazyGetDiametersQuery()

  const getCurrentCriteria = () => {
    return {
      tyreType: props.tyreType,
      width: props.width,
      aspectRatio: props.aspectRatio,
      diameter: props.diameter,
    }
  }

  const onChangeHandler = (value: string | null, name: string) => {
    const newCriteria = {
      ...getCurrentCriteria(),
      [name]: Number(value),
    }

    props.onCriteriaChange(newCriteria)
  }

  const onWidthChangeHandler = (value: string | null, name: string) => {
    const newCriteria = {
      ...getCurrentCriteria(),
      [name]: Number(value),
      aspectRatio: undefined,
      diameter: undefined,
    }

    props.onCriteriaChange(newCriteria)
  }

  const onAspectRatioChangeHandler = (value: string | null, name: string) => {
    const newCriteria = {
      ...getCurrentCriteria(),
      [name]: Number(value),
      diameter: undefined,
    }

    props.onCriteriaChange(newCriteria)
  }

  const onDiametersChangeHandler = (value: string | null, name: string) => {
    const newCriteria = {
      ...getCurrentCriteria(),
      [name]: Number(value),
    }

    props.onCriteriaChange(newCriteria)
  }

  useEffect(() => {
    if (props.width && props.aspectRatio) {
      fetchDiameters({
        width: props.width,
        aspectRatio: props.aspectRatio,
      })
    }
  }, [fetchDiameters, props.aspectRatio, props.width])

  const tyreSizes: NameValue[] = tyreSizesData?.map((tyreSize) => ({
    name: tyreSize.width.toString(),
    value: tyreSize.width.toString(),
  })) || []

  const aspectRatios: NameValue[] = tyreSizesData?.find((tyreSize) => tyreSize.width === props.width)
    ?.aspectRatios.map((aspectRatio) => ({
      name: aspectRatio.toString(),
      value: aspectRatio.toString(),
    })) || []

  const diameters: NameValue[] = diametersData?.data?.map((diameter) => ({
    name: diameter.toString(),
    value: diameter.toString(),
  })) || []

  const width = props.width ? props.width?.toString() : ''
  const aspectRatio = props.aspectRatio ? props.aspectRatio?.toString() : ''
  const diameter = props.diameter ? props.diameter?.toString() : ''

  return (
    <div className={styles.component}>
      <FilterableSelect
        name={'tyreType'}
        options={[
          { name: t('shop:allTyres'), value: '0' },
          { name: t('shop:summerTyres'), value: '1' },
          { name: t('shop:winterTyres'), value: '2' },
        ]}
        value={props.tyreType.toString()}
        onChange={onChangeHandler}
        enableFiltering={false}
      />
      <FilterableSelect
        name={'width'}
        placeholder={t('shop:selectWidth')}
        options={tyreSizes}
        value={width}
        onChange={onWidthChangeHandler}
      />
      <FilterableSelect
        name={'aspectRatio'}
        placeholder={t('shop:selectAspectRatio')}
        options={aspectRatios}
        value={aspectRatio}
        onChange={onAspectRatioChangeHandler}
        disabled={!props.width}
      />
      <FilterableSelect
        name={'diameter'}
        placeholder={t('shop:selectDiameter')}
        options={diameters}
        value={diameter}
        onChange={onDiametersChangeHandler}
        disabled={!props.aspectRatio}
      />
    </div>
  )
}

export default TyreSizePicker